import {LayoutVo} from '../../common/shared/api/karina/vo/LayoutVo.interface';
import {DayOfWeekStaticLandingPageVo} from '../../common/shared/api/karina/vo/page/DayOfWeekStaticLandingPageVo.interface';
import {EventStaticLandingPageVo} from '../../common/shared/api/karina/vo/page/EventStaticLandingPageVo.interface';
import {FreeSeriesStaticLandingPageVo} from '../../common/shared/api/karina/vo/page/FreeSeriesStaticLandingPageVo.interface';
import {BaseSectionVo} from '../../common/shared/api/karina/vo/section/BaseSectionVo.interface';
import {GenreStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/GenreStaticLandingPageVo.interface';
import {OriginalStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/OriginalStaticLandingPageVo.interface';
import {RankingStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/RankingStaticLandingPageVo.interface';
import {RankingThemeStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/RankingThemeStaticLandingPageVo.interface';
import {TodayNewStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/TodayNewStaticLandingPageVo.interface';
import {TodayUpInvenStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/TodayUpInvenStaticLandingPageVo.interface';
import {TodayUpStaticLandingPageVo} from '../../common/shared/api/nicolas/vo/page/TodayUpStaticLandingPageVo.interface';
import {generateStaticLandingLayoutId} from '../../staticLanding/generateStaticLandingLayoutId';
import {
  DayOfWeekBmType,
  Layout,
  LayoutType,
  Maybe,
  Section,
  StaticLandingDayOfWeekParamInput,
  StaticLandingEventParamInput,
  StaticLandingFreeSeriesParamInput,
  StaticLandingGenreParamInput,
  StaticLandingOriginalParamInput,
  StaticLandingRankingParamInput,
  StaticLandingRankingThemeParamInput,
  StaticLandingTodayNewParamInput,
  StaticLandingTodayUpParamInput,
  StaticLandingType,
  User,
} from '../generated/types';
import {createCardSectionWithBannerDto} from './CardSection';
import {createDisplayAd} from './DisplayAd';
import {createDisplayAdSection} from './DisplayAdSection';
import {createPromotionBannerSection} from './PromotionBannerSection';
import {createSectionWithSectionVo} from './Section';
import {createStaticLandingDayOfWeekSection} from './StaticLandingDayOfWeekSection';
import {createStaticLandingEventSection} from './StaticLandingEventSection';
import {createStaticLandingFreeSeriesSection} from './StaticLandingFreeSeriesSection';
import {createStaticLandingGenreSection} from './StaticLandingGenreSection';
import {createStaticLandingOriginalSection} from './StaticLandingOriginalSection';
import {createStaticLandingRankingSection} from './StaticLandingRankingSection';
import {createStaticLandingRankingThemeSection} from './StaticLandingRankingThemeSection';
import {createStaticLandingTodayNewSection} from './StaticLandingTodayNewSection';
import {createStaticLandingTodayUpSection} from './StaticLandingTodayUpSection';
import {createTopSection} from './TopSection';

export function createLayoutWithLayoutVo(
  userInfo: Maybe<User>,
  {
    layoutVo,
    type,
    screenUid,
  }: {
    layoutVo: LayoutVo;
    type?: Maybe<LayoutType>;
    screenUid?: number;
  }
): Layout {
  const id = `layout-${screenUid}`;
  const sections = (layoutVo?.layout?.map((sectionVo: BaseSectionVo) =>
    createSectionWithSectionVo(userInfo, {
      sectionVo,
      layoutVo,
      layoutId: id,
    })
  ) || []) as Section[];

  return {
    id,
    type: type || LayoutType.Layout,
    sections,
    screenUid,
  };
}

export function createLayout({
  userInfo,
  param,
}: {
  userInfo: Maybe<User>;
  param:
    | {
        todayNewStaticLandingPageVo: TodayNewStaticLandingPageVo;
        input: StaticLandingTodayNewParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        rankingStaticLandingPageVo: RankingStaticLandingPageVo;
        input: StaticLandingRankingParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        rankingThemeStaticLandingPageVo: RankingThemeStaticLandingPageVo;
        input: StaticLandingRankingThemeParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        originalStaticLandingPageVo: OriginalStaticLandingPageVo;
        input: StaticLandingEventParamInput;
      }
    | {
        genreStaticLandingPageVo: GenreStaticLandingPageVo;
        input: StaticLandingGenreParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        dayOfWeekStaticLandingPageVo: DayOfWeekStaticLandingPageVo;
        input: StaticLandingDayOfWeekParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        freeSeriesStaticLandingPageVo: FreeSeriesStaticLandingPageVo;
        input: StaticLandingFreeSeriesParamInput;
        type?: Maybe<LayoutType>;
      }
    | {
        eventStaticLandingPageVo: EventStaticLandingPageVo;
        input: StaticLandingEventParamInput;
      }
    | {
        todayUpStaticLandingPageVo: TodayUpStaticLandingPageVo;
        todayUpInvenStaticLandingPageVo?: TodayUpInvenStaticLandingPageVo;
        input: StaticLandingTodayUpParamInput;
      };
}): Layout {
  if ('todayNewStaticLandingPageVo' in param) {
    return withTodayNew(userInfo, param);
  } else if ('rankingStaticLandingPageVo' in param) {
    return withRanking(userInfo, param);
  } else if ('rankingThemeStaticLandingPageVo' in param) {
    return withRankingTheme(userInfo, param);
  } else if ('originalStaticLandingPageVo' in param) {
    return withOriginal(userInfo, param);
  } else if ('genreStaticLandingPageVo' in param) {
    return withGenre(userInfo, param);
  } else if ('dayOfWeekStaticLandingPageVo' in param) {
    return withDayOfWeek(userInfo, param);
  } else if ('freeSeriesStaticLandingPageVo' in param) {
    return withFreeSeries(userInfo, param);
  } else if ('eventStaticLandingPageVo' in param) {
    return withEvent(userInfo, param);
  } else {
    return withTodayUp(userInfo, param);
  }
}

function withTodayNew(
  userInfo: Maybe<User>,
  {
    todayNewStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    todayNewStaticLandingPageVo: TodayNewStaticLandingPageVo;
    input: StaticLandingTodayNewParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    categoryUid: input.categoryUid ?? vo.selected_cat_opt?.param ?? '',
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.TodayNew,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingTodayNewSection(userInfo, {
        todayNewStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

function withRanking(
  userInfo: Maybe<User>,
  {
    rankingStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    rankingStaticLandingPageVo: RankingStaticLandingPageVo;
    input: StaticLandingRankingParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
    rankingType: input.rankingType ?? vo.selected_rank_opt?.param ?? '',
  };
  const id = generateStaticLandingLayoutId({
    landingListType: StaticLandingType.Ranking,
    layoutType: type,
    input: resolvedInput,
  });
  let withOperationArea = false;

  const sections: Section[] = [];

  if (type === LayoutType.Layout && vo.section_promotion_banner) {
    sections.push(
      createPromotionBannerSection({
        layoutId: id,
        promotionBanner: vo.section_promotion_banner,
      })
    );
  }

  if (vo.section_top_v2 && vo.section_top_v2.length > 0) {
    sections.push(
      createTopSection({
        layoutId: id,
        bannerList: vo.section_top_v2,
      })
    );
    withOperationArea = true;
  }

  if (type === LayoutType.Layout && vo.display_ad) {
    sections.push(
      createDisplayAdSection({
        layoutId: id,
        displayAd: createDisplayAd(vo.display_ad),
      })
    );
  }

  if (vo.section_card_banner && vo.section_card_banner.length > 0) {
    sections.push(
      createCardSectionWithBannerDto(userInfo, {
        layoutId: id,
        bannerList: vo.section_card_banner,
      })
    );
    withOperationArea = true;
  }

  sections.push(
    createStaticLandingRankingSection(userInfo, {
      rankingStaticLandingPageVo: vo,
      input,
      type,
      withOperationArea,
    })
  );
  return {
    id,
    type,
    sections,
  };
}

function withRankingTheme(
  userInfo: Maybe<User>,
  {
    rankingThemeStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    rankingThemeStaticLandingPageVo: RankingThemeStaticLandingPageVo;
    input: StaticLandingRankingThemeParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  const resolvedInput = {
    ...input,
    filterType: input.filterType ?? vo.selected_filter_opt?.param ?? '',
    sortType: input.sortType ?? vo.selected_rank_opt?.param ?? '',
  };
  const id = generateStaticLandingLayoutId({
    landingListType: StaticLandingType.RankingTheme,
    layoutType: type,
    input: resolvedInput,
  });

  /**
   * 단행본 랭킹 테마 서브탭은 프로모션 배너를 별도 섹션으로 분리하지 않고
   * 랭킹 테마 섹션 내부에서 함께 그린다 (StaticLandingRankingTheme 컴포넌트 참고).
   */
  return {
    id,
    type,
    sections: [
      createStaticLandingRankingThemeSection(userInfo, {
        rankingThemeStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

function withOriginal(
  userInfo: Maybe<User>,
  {
    originalStaticLandingPageVo: vo,
    input,
  }: {
    originalStaticLandingPageVo: OriginalStaticLandingPageVo;
    input: StaticLandingOriginalParamInput;
  }
): Layout {
  const type = LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
    sortType: input.sortType ?? vo.selected_sort_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.Original,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingOriginalSection(userInfo, {
        originalStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

function withGenre(
  userInfo: Maybe<User>,
  {
    genreStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    genreStaticLandingPageVo: GenreStaticLandingPageVo;
    input: StaticLandingGenreParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
    sortType: input.sortType ?? vo.selected_sort_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.Genre,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingGenreSection(userInfo, {
        genreStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

function withTodayUp(
  userInfo: Maybe<User>,
  {
    todayUpStaticLandingPageVo: vo,
    todayUpInvenStaticLandingPageVo,
    input,
  }: {
    todayUpStaticLandingPageVo: TodayUpStaticLandingPageVo;
    todayUpInvenStaticLandingPageVo?: TodayUpInvenStaticLandingPageVo;
    input: StaticLandingTodayUpParamInput;
  }
): Layout {
  const type = LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    categoryUid: input.categoryUid ?? vo.selected_cat_opt?.param ?? '',
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.TodayUp,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingTodayUpSection(userInfo, {
        todayUpStaticLandingPageVo: vo,
        todayUpInvenStaticLandingPageVo,
        input,
        type,
      }),
    ],
  };
}

export function withDayOfWeek(
  userInfo: Maybe<User>,
  {
    dayOfWeekStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    dayOfWeekStaticLandingPageVo: DayOfWeekStaticLandingPageVo;
    input: StaticLandingDayOfWeekParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    subcategoryUid: input.subcategoryUid ?? vo.selected_subcat_opt?.param ?? '',
    dayTabUid: input.dayTabUid ?? vo.selected_tab_opt?.param ?? '',
    bmType: input.bmType ?? (vo.selected_bm_opt?.param as DayOfWeekBmType),
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.DayOfWeek,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingDayOfWeekSection(userInfo, {
        dayOfWeekStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

export function withFreeSeries(
  userInfo: Maybe<User>,
  {
    freeSeriesStaticLandingPageVo: vo,
    input,
    type: _type,
  }: {
    freeSeriesStaticLandingPageVo: FreeSeriesStaticLandingPageVo;
    input: StaticLandingFreeSeriesParamInput;
    type?: Maybe<LayoutType>;
  }
): Layout {
  const type = _type ?? LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    tabUid: input.tabUid ?? vo.selected_tab_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.FreeSeries,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingFreeSeriesSection(userInfo, {
        freeSeriesStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}

export function withEvent(
  userInfo: Maybe<User>,
  {
    eventStaticLandingPageVo: vo,
    input,
  }: {
    eventStaticLandingPageVo: EventStaticLandingPageVo;
    input: StaticLandingEventParamInput;
  }
): Layout {
  const type = LayoutType.Landing;
  // input에 값이 없으면 API 응답의 실제 선택된 값 사용
  const resolvedInput = {
    ...input,
    categoryUid: input.categoryUid ?? vo.selected_cat_opt?.param ?? '',
  };
  return {
    id: generateStaticLandingLayoutId({
      landingListType: StaticLandingType.Event,
      layoutType: type,
      input: resolvedInput,
    }),
    type,
    sections: [
      createStaticLandingEventSection(userInfo, {
        eventStaticLandingPageVo: vo,
        input,
        type,
      }),
    ],
  };
}
